<template>
  <div v-if="isLocked" class="lock-workstation-overlay" @click.self="focusInput">
    <div
      class="lock-workstation-card"
      :class="{ 'lock-card-shake': isShaking }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lock-title"
    >
      <!-- Lock Header with Glowing Icon -->
      <div class="lock-card-header">
        <div class="lock-icon-badge">
          <svg
            class="lock-svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="4"
              y="10"
              width="16"
              height="11"
              rx="3"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M7 10V7a5 5 0 0110 0v3"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <circle cx="12" cy="15.5" r="1.5" fill="currentColor" />
          </svg>
        </div>
        <h2 id="lock-title" class="lock-title">Workstation Locked</h2>
        <p class="lock-subtitle">
          Terminal is secured. Enter your login Password PIN to unlock.
        </p>
      </div>

      <!-- Current Operator Details Pill -->
      <div v-if="operatorName" class="lock-operator-chip">
        <div class="lock-operator-avatar">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2" />
            <path
              d="M5 20c0-3.866 3.134-7 7-7s7 3.134 7 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div class="lock-operator-info">
          <span class="lock-operator-role">Current Operator</span>
          <span class="lock-operator-name">{{ operatorName }}</span>
        </div>
      </div>

      <!-- PIN Input Area -->
      <div class="lock-pin-area">
        <label class="lock-pin-label">Password PIN</label>
        <div class="lock-pin-box-wrap" @click="focusInput">
          <input
            ref="pinInput"
            type="password"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            class="lock-pin-native-input"
            autocomplete="off"
            tabindex="-1"
            @input="onNativeInput"
          />
          <div
            v-for="idx in 4"
            :key="'pin-box-' + idx"
            class="lock-pin-digit-box"
            :class="{
              filled: pinLength >= idx,
              focused: pinLength === idx - 1,
              error: !!errorMessage
            }"
          >
            <span v-if="pinLength >= idx" class="lock-pin-dot">●</span>
          </div>
        </div>
      </div>

      <!-- Inline Error Message -->
      <div v-if="errorMessage" class="lock-error-alert" role="alert">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
          <line
            x1="12"
            y1="8"
            x2="12"
            y2="12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <circle cx="12" cy="16" r="1" fill="currentColor" />
        </svg>
        <span>{{ errorMessage }}</span>
      </div>

      <!-- Touch Keypad for POS screens -->
      <div class="lock-numpad">
        <button
          v-for="(key, index) in keyPadKeys"
          :key="'pad-key-' + index"
          type="button"
          class="lock-numpad-btn"
          :class="{ 'lock-numpad-btn--muted': key.type !== 'digit' }"
          @click.prevent.stop="onKeypadPress(key)"
        >
          <span v-if="key.type === 'digit'">{{ key.label }}</span>
          <span v-else-if="key.type === 'clear'">C</span>
          <span v-else-if="key.type === 'backspace'">⌫</span>
        </button>
      </div>

      <!-- Action Button -->
      <button
        type="button"
        class="lock-submit-btn"
        :disabled="isLoading || pinLength === 0"
        @click.prevent.stop="handleUnlock"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm mr-2"
          role="status"
          aria-hidden="true"
        ></span>
        <span>{{ isLoading ? "Verifying..." : "Unlock Workstation" }}</span>
      </button>

      <!-- Switch User / Logout Option -->
      <div class="lock-footer-links">
        <button
          type="button"
          class="lock-logout-link"
          @click.prevent.stop="confirmLogout"
        >
          Switch User / Logout
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import PosService from "@/services/PosService.js";
import UserService from "@/services/UserService.js";
import { EventBus } from "@/main.js";

const generateRandomSessionKey = () => {
  try {
    if (window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(4);
      window.crypto.getRandomValues(arr);
      return Array.from(arr, (val) => val.toString(16)).join("");
    }
  } catch (e) {}
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

const STORAGE_ENCRYPTION_KEY = "BR_WORKSTATION_LOCK_SECURE_STORAGE_KEY";

const SecureStorage = {
  encrypt(value) {
    if (value === null || value === undefined) return "";
    try {
      const payload = typeof value === "object" ? JSON.stringify(value) : String(value);
      return CryptoJS.AES.encrypt(payload, STORAGE_ENCRYPTION_KEY).toString();
    } catch (e) {
      return String(value);
    }
  },

  decrypt(cipherText) {
    if (!cipherText || typeof cipherText !== "string") return null;
    const trimmed = cipherText.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;

    if (trimmed.startsWith("U2FsdGVkX1")) {
      try {
        const bytes = CryptoJS.AES.decrypt(trimmed, STORAGE_ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted && decrypted.length > 0 ? decrypted : null;
      } catch (e) {
        return null;
      }
    }
    return trimmed;
  },

  getItem(key, isEncrypted = false) {
    try {
      if (typeof window === "undefined" || !window.localStorage) return null;
      const raw = window.localStorage.getItem(key);
      if (raw === null || raw === undefined) return null;

      const trimmed = String(raw).trim();
      if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;

      return isEncrypted ? this.decrypt(trimmed) : trimmed;
    } catch (e) {
      return null;
    }
  },

  setEncryptedItem(key, value) {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      if (value === null || value === undefined) {
        window.localStorage.removeItem(key);
        return;
      }
      const encrypted = this.encrypt(value);
      window.localStorage.setItem(key, encrypted);
    } catch (e) {}
  },

  setItem(key, value, encrypt = false) {
    if (encrypt) {
      this.setEncryptedItem(key, value);
    } else {
      try {
        if (typeof window === "undefined" || !window.localStorage) return;
        if (value === null || value === undefined) {
          window.localStorage.removeItem(key);
          return;
        }
        const valToStore = typeof value === "object" ? JSON.stringify(value) : String(value);
        window.localStorage.setItem(key, valToStore);
      } catch (e) {}
    }
  },

  removeItem(key) {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(key);
      }
    } catch (e) {}
  },

  clear() {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.clear();
      }
    } catch (e) {}
  },

  getJSON(key, isEncrypted = false) {
    try {
      const dataStr = this.getItem(key, isEncrypted);
      if (!dataStr || typeof dataStr !== "string") return null;

      const trimmed = dataStr.trim();
      if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;

      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  },

  isWorkstationLocked() {
    try {
      const secureLock = this.getItem("SECURE_WORKSTATION_LOCKED", true);
      if (secureLock === "true") return true;

      const legacyLock = this.getItem("IS_WORKSTATION_LOCKED", false);
      return legacyLock === "true";
    } catch (e) {
      return false;
    }
  },

  setWorkstationLocked(isLocked) {
    if (isLocked) {
      this.setEncryptedItem("SECURE_WORKSTATION_LOCKED", "true");
      this.setItem("IS_WORKSTATION_LOCKED", "true", false);
    } else {
      this.removeItem("SECURE_WORKSTATION_LOCKED");
      this.removeItem("IS_WORKSTATION_LOCKED");
    }
  },

  getUserInfo() {
    try {
      const parsed = this.getJSON("USER_INFO", false) || this.getJSON("SECURE_USER_INFO", true);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
      }

      return {
        first_name:
          parsed.first_name && typeof parsed.first_name === "string"
            ? parsed.first_name.trim()
            : "",
        last_name:
          parsed.last_name && typeof parsed.last_name === "string"
            ? parsed.last_name.trim()
            : "",
        username:
          parsed.username && typeof parsed.username === "string"
            ? parsed.username.trim()
            : "",
        user_id:
          parsed.user_id !== null &&
          parsed.user_id !== undefined &&
          parsed.user_id !== ""
            ? parsed.user_id
            : null,
      };
    } catch (e) {
      return null;
    }
  },
};

export default {
  name: "LockWorkstationPopup",
  data() {
    let initiallyLocked = false;
    try {
      initiallyLocked = SecureStorage.isWorkstationLocked();
    } catch (e) {
      initiallyLocked = false;
    }
    return {
      isLocked: initiallyLocked,
      pinLength: 0,
      encryptedPin: "",
      sessionKey: generateRandomSessionKey(),
      errorMessage: "",
      isLoading: false,
      isShaking: false,
      keyPadKeys: [
        { type: "digit", label: "1" },
        { type: "digit", label: "2" },
        { type: "digit", label: "3" },
        { type: "digit", label: "4" },
        { type: "digit", label: "5" },
        { type: "digit", label: "6" },
        { type: "digit", label: "7" },
        { type: "digit", label: "8" },
        { type: "digit", label: "9" },
        { type: "clear", label: "C" },
        { type: "digit", label: "0" },
        { type: "backspace", label: "⌫" },
      ],
    };
  },
  computed: {
    currentUserInfo() {
      const info = SecureStorage.getUserInfo();
      if (!info || typeof info !== "object") {
        return {
          first_name: "",
          last_name: "",
          username: "",
          user_id: null,
        };
      }
      return info;
    },
    operatorName() {
      const info = this.currentUserInfo;
      if (info && info.first_name) {
        return `${info.first_name} ${info.last_name || ""}`.trim();
      }
      return (info && info.username) || "";
    },
    operatorUserId() {
      const info = this.currentUserInfo;
      return info && info.user_id !== undefined && info.user_id !== null
        ? info.user_id
        : null;
    },
    operatorUsername() {
      const info = this.currentUserInfo;
      return (info && info.username) || "";
    },
  },
  watch: {
    isLocked(val) {
      if (val) {
        this.addGlobalListeners();
        this.$nextTick(() => {
          this.focusInput();
        });
      } else {
        this.removeGlobalListeners();
      }
    },
  },
  created() {
    this.checkInitialLock();
    EventBus.$on("LOCK_WORKSTATION", this.onLockEvent);
    EventBus.$on("UNLOCK_WORKSTATION", this.unlockSuccess);
  },
  mounted() {
    if (this.isLocked) {
      this.addGlobalListeners();
      this.$nextTick(() => {
        this.focusInput();
      });
    }
    window.addEventListener("storage", this.onStorageChange);
  },
  beforeDestroy() {
    EventBus.$off("LOCK_WORKSTATION", this.onLockEvent);
    EventBus.$off("UNLOCK_WORKSTATION", this.unlockSuccess);
    this.removeGlobalListeners();
    window.removeEventListener("storage", this.onStorageChange);
  },
  methods: {
    onStorageChange(e) {
      if (
        e &&
        (e.key === "IS_WORKSTATION_LOCKED" ||
          e.key === "SECURE_WORKSTATION_LOCKED")
      ) {
        const isLocked = SecureStorage.isWorkstationLocked();
        if (isLocked) {
          this.isLocked = true;
          this.addGlobalListeners();
          this.focusInput();
        } else {
          this.isLocked = false;
          this.removeGlobalListeners();
        }
      }
    },
    addGlobalListeners() {
      this.removeGlobalListeners();
      window.addEventListener("keydown", this.handleGlobalKeyDown, true);
    },
    removeGlobalListeners() {
      window.removeEventListener("keydown", this.handleGlobalKeyDown, true);
    },
    checkInitialLock() {
      try {
        const isLockedStorage = SecureStorage.isWorkstationLocked();
        if (isLockedStorage) {
          this.isLocked = true;
          this.addGlobalListeners();
          this.$nextTick(() => {
            this.focusInput();
          });
        }
      } catch (e) {
        console.error("checkInitialLock error:", e);
      }
    },
    onLockEvent() {
      SecureStorage.setWorkstationLocked(true);
      this.isLocked = true;
      this.clearSecurePin();
      this.sessionKey = this.generateSessionKey();
      this.errorMessage = "";
      this.addGlobalListeners();
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    generateSessionKey() {
      return generateRandomSessionKey();
    },
    getDecryptedPin() {
      if (!this.encryptedPin || !this.sessionKey) return "";
      try {
        const bytes = CryptoJS.AES.decrypt(this.encryptedPin, this.sessionKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted || "";
      } catch (e) {
        return "";
      }
    },
    setSecurePin(plainText) {
      const cleaned = String(plainText || "")
        .replace(/\D/g, "")
        .slice(0, 4);
      this.pinLength = cleaned.length;
      if (cleaned.length > 0) {
        this.encryptedPin = CryptoJS.AES.encrypt(
          cleaned,
          this.sessionKey
        ).toString();
      } else {
        this.encryptedPin = "";
      }
      if (this.$refs.pinInput) {
        this.$refs.pinInput.value = "";
      }
    },
    clearSecurePin() {
      this.encryptedPin = "";
      this.pinLength = 0;
      if (this.$refs.pinInput) {
        try {
          this.$refs.pinInput.value = "";
        } catch (e) {}
      }
    },
    clearSensitiveBuffers() {
      // Immediately wipes any lingering sensitive data from memory
      this.clearSecurePin();
    },
    extractErrorMessage(err, defaultMsg = "Authentication failed") {
      if (!err) return defaultMsg;
      if (err.response && err.response.data && err.response.data.message) {
        return err.response.data.message;
      }
      if (
        err.response &&
        err.response.data &&
        typeof err.response.data.error === "string"
      ) {
        return err.response.data.error;
      }
      if (err.response) {
        if (err.response.status === 401) {
          return "Incorrect Password PIN. Access denied.";
        }
        if (err.response.status === 403) {
          return "Unauthorized. You do not have permission to unlock this workstation.";
        }
        if (err.response.status === 404) {
          return "Workstation unlock service endpoint not found. Contact administrator.";
        }
        if (err.response.status >= 500) {
          return "Server error encountered during authentication. Please try again.";
        }
      }
      if (err.message) {
        if (err.message.toLowerCase().includes("network")) {
          return "Network connection issue. Please check your network and try again.";
        }
        if (err.message.toLowerCase().includes("timeout")) {
          return "Authentication request timed out. Please try again.";
        }
        return err.message;
      }
      return defaultMsg;
    },
    focusInput() {
      this.$nextTick(() => {
        if (
          this.$refs.pinInput &&
          typeof this.$refs.pinInput.focus === "function"
        ) {
          try {
            this.$refs.pinInput.focus();
            if (typeof this.$refs.pinInput.setSelectionRange === "function") {
              const caret = this.pinLength;
              this.$refs.pinInput.setSelectionRange(caret, caret);
            }
          } catch (e) {}
        }
      });
    },
    onNativeInput(e) {
      this.errorMessage = "";
      const val = e && e.target ? e.target.value : "";
      const digits = String(val || "").replace(/\D/g, "");
      if (digits.length > 0) {
        const current = this.getDecryptedPin();
        const combined = (current + digits).slice(0, 4);
        this.setSecurePin(combined);
        if (this.pinLength === 4) {
          this.$nextTick(() => {
            this.handleUnlock();
          });
        }
      }
      if (this.$refs.pinInput) {
        this.$refs.pinInput.value = "";
      }
    },
    onKeypadPress(key) {
      if (this.isLoading) return;
      this.errorMessage = "";
      const current = this.getDecryptedPin();
      if (key.type === "digit") {
        if (this.pinLength < 4) {
          const updated = (current + key.label).slice(0, 4);
          this.setSecurePin(updated);
          if (this.pinLength === 4) {
            this.$nextTick(() => {
              this.handleUnlock();
            });
          }
        }
      } else if (key.type === "clear") {
        this.clearSecurePin();
        this.focusInput();
      } else if (key.type === "backspace") {
        if (this.pinLength > 0) {
          this.setSecurePin(current.slice(0, -1));
        }
        this.focusInput();
      }
    },
    handleGlobalKeyDown(e) {
      if (!this.isLocked || !e) return;

      // Always block Escape from exiting the workstation lock
      if (e.key === "Escape" || e.code === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // If verifying or unlocking, ignore further keypresses
      if (this.isLoading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Enter key -> Submit unlock
      if (e.key === "Enter" || e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        e.stopPropagation();
        this.handleUnlock();
        return;
      }

      // Backspace / Delete -> remove last digit
      if (
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.code === "Backspace" ||
        e.code === "Delete"
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (this.pinLength > 0) {
          const current = this.getDecryptedPin();
          this.setSecurePin(current.slice(0, -1));
        }
        return;
      }

      // Detect digit 0-9 from top row or numeric keypad
      let digit = null;
      if (/^[0-9]$/.test(e.key)) {
        digit = e.key;
      } else if (
        e.code &&
        e.code.startsWith("Digit") &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey
      ) {
        const d = e.code.replace("Digit", "");
        if (/^[0-9]$/.test(d)) {
          digit = d;
        }
      } else if (e.code && e.code.startsWith("Numpad")) {
        const d = e.code.replace("Numpad", "");
        if (/^[0-9]$/.test(d)) {
          digit = d;
        }
      }

      if (digit !== null) {
        e.preventDefault();
        e.stopPropagation();
        if (this.pinLength < 4) {
          const current = this.getDecryptedPin();
          this.setSecurePin((current + digit).slice(0, 4));
          if (this.pinLength === 4) {
            this.$nextTick(() => {
              this.handleUnlock();
            });
          }
        }
        return;
      }

      // Prevent other printable keys (letters, symbols) from interfering or typing into background
      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    async handleUnlock() {
      if (this.isLoading) return;

      const rawPin = this.getDecryptedPin();
      if (!rawPin || rawPin.length < 4) {
        this.triggerError("Please enter 4-digit Password PIN");
        return;
      }

      this.isLoading = true;
      this.errorMessage = "";

      const userId = this.operatorUserId;
      const username = this.operatorUsername;

      if (!userId && !username) {
        this.triggerError(
          "User session not found in storage. Please log in again."
        );
        return;
      }

      let isSuccess = false;
      let failureReason = "";

      try {
        // 1. Try dedicated UnlockWorkstation endpoint
        try {
          const response = await PosService.UnlockWorkstation({
            password_pin: rawPin,
            user_id: userId,
          });
          const data = response && response.data;
          if (data && data.status) {
            isSuccess = true;
          } else if (data && data.message) {
            failureReason = data.message;
          } else {
            failureReason = "Incorrect Password PIN";
          }
        } catch (apiErr) {
          // Provide user-visible error feedback rather than silently logging
          failureReason = this.extractErrorMessage(
            apiErr,
            "Workstation unlock verification failed"
          );
        }

        // 2. Fallback check: CheckUserPassword (checks password and passwordpin)
        if (!isSuccess && username) {
          try {
            const fallbackResponse = await UserService.CheckUserPassword({
              username: username,
              password: rawPin,
            });
            const fallbackData = fallbackResponse && fallbackResponse.data;
            if (fallbackData && fallbackData.status) {
              isSuccess = true;
              failureReason = "";
            } else if (fallbackData && fallbackData.message) {
              failureReason = fallbackData.message;
            }
          } catch (fallbackErr) {
            // Provide informative error feedback for user
            const fallbackMsg = this.extractErrorMessage(
              fallbackErr,
              failureReason || "Authentication failed"
            );
            if (fallbackMsg) {
              failureReason = fallbackMsg;
            }
          }
        }

        if (isSuccess) {
          this.unlockSuccess();
        } else {
          this.triggerError(
            failureReason || "Incorrect Password PIN. Please try again."
          );
        }
      } catch (error) {
        const errorFeedback = this.extractErrorMessage(
          error,
          "Failed to unlock workstation. Please try again."
        );
        this.triggerError(errorFeedback);
      } finally {
        this.isLoading = false;
        // Obfuscate / clear sensitive credentials from component state immediately after use
        this.clearSensitiveBuffers();
      }
    },
    unlockSuccess() {
      this.isLocked = false;
      this.clearSecurePin();
      this.sessionKey = this.generateSessionKey();
      this.errorMessage = "";
      this.removeGlobalListeners();
      SecureStorage.setWorkstationLocked(false);
      if (this.$toasted && typeof this.$toasted.show === "function") {
        this.$toasted.show("Workstation unlocked", {
          duration: 2500,
          type: "success",
        });
      }
      this.$emit("unlocked");
    },
    triggerError(msg) {
      const displayMsg = msg || "Incorrect Password PIN. Please try again.";
      this.errorMessage = displayMsg;
      this.isShaking = true;
      setTimeout(() => {
        this.isShaking = false;
      }, 500);
      this.clearSecurePin();
      this.focusInput();

      if (this.$toasted && typeof this.$toasted.show === "function") {
        this.$toasted.show(displayMsg, {
          duration: 3500,
          type: "error",
        });
      }
    },
    confirmLogout() {
      const confirmed = window.confirm(
        "Are you sure you want to log out? Any unsaved progress will be cleared."
      );
      if (!confirmed) return;

      this.isLocked = false;
      this.clearSecurePin();
      this.removeGlobalListeners();
      SecureStorage.setWorkstationLocked(false);
      SecureStorage.clear();
      this.$router.push({ path: "/" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.lock-workstation-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(11, 15, 25, 0.88);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  animation: lockFadeIn 0.25s ease-out;
}

@keyframes lockFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lock-workstation-card {
  width: 100%;
  max-width: 380px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 28px 24px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: lockCardScale 0.25s ease-out;
}

@keyframes lockCardScale {
  from {
    transform: scale(0.94);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.lock-card-shake {
  animation: lockShake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

@keyframes lockShake {
  10%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  80% {
    transform: translateX(4px);
  }
  30%,
  50%,
  70% {
    transform: translateX(-6px);
  }
  40%,
  60% {
    transform: translateX(6px);
  }
}

.lock-card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
}

.lock-icon-badge {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: var(--blue);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.18);
}

.lock-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
}

.lock-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
  max-width: 280px;
}

.lock-operator-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 30px;
  margin-bottom: 18px;
}

.lock-operator-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #ffffff;
  color: var(--blue);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.lock-operator-info {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.lock-operator-role {
  font-size: 10px;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
}

.lock-operator-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.25;
}

.lock-pin-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
}

.lock-pin-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
}

.lock-pin-box-wrap {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 46px);
  gap: 10px;
  cursor: text;
}

.lock-pin-native-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  cursor: text;
  z-index: 2;
}

.lock-pin-digit-box {
  width: 46px;
  height: 52px;
  background: #f8fafc;
  border: 1.5px solid #cbd5e1;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.16s ease;
}

.lock-pin-digit-box.focused {
  border-color: var(--blue);
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}

.lock-pin-digit-box.filled {
  background: #ffffff;
  border-color: #94a3b8;
}

.lock-pin-digit-box.error {
  border-color: #ef4444 !important;
  background: #fef2f2 !important;
}

.lock-pin-dot {
  font-size: 20px;
  color: #1e293b;
  line-height: 1;
}

.lock-error-alert {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 12px;
  text-align: center;
}

.lock-numpad {
  width: 100%;
  max-width: 250px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.lock-numpad-btn {
  height: 44px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.14s ease;
  user-select: none;
  -webkit-user-select: none;
}

.lock-numpad-btn:hover {
  background: #f8fafc;
  border-color: var(--blue);
}

.lock-numpad-btn:active {
  transform: scale(0.96);
  background: #e2e8f0;
}

.lock-numpad-btn--muted {
  background: #f1f5f9;
  color: #64748b;
  font-size: 14px;
}

.lock-submit-btn {
  width: 100%;
  max-width: 250px;
  height: 44px;
  background: var(--blue);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.16s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.lock-submit-btn:hover:not(:disabled) {
  filter: brightness(0.92);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
}

.lock-submit-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.lock-submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.lock-footer-links {
  margin-top: 14px;
  text-align: center;
}

.lock-logout-link {
  background: none;
  border: none;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.15s ease;
}

.lock-logout-link:hover {
  color: #dc2626;
}
</style>



// WEBPACK FOOTER //
// src/components/Partials/LockWorkstationPopup.vue